import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Landing from "@/pages/Landing";
import ProductionAccess from "@/pages/ProductionAccess";
import EmployeeTimesheet from "@/pages/EmployeeTimesheet";
import AdminLogin from "@/pages/AdminLogin";
import WorkOrderLogin from "@/pages/WorkOrderLogin";
import StoreLogin from "@/pages/StoreLogin";
import ResetPassword from "@/pages/ResetPassword";
import AdminLayout from "@/components/AdminLayout";
import WorkOrderLayout from "@/components/WorkOrderLayout";
import StoreLayout from "@/components/StoreLayout";
import Dashboard from "@/pages/Dashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import CostCalculator from "@/pages/CostCalculator";
import CompletionHistory from "@/pages/CompletionHistory";
import Machines from "@/pages/Machines";
import Reports from "@/pages/Reports";
import WorkOrderCreationNew from "@/pages/WorkOrderCreationNew";
import WorkOrderProgress from "@/pages/WorkOrderProgress";
import SavedWorkOrders from "@/pages/SavedWorkOrders";
import ViewWorkOrder from "@/pages/ViewWorkOrder";
import EditWorkOrderFull from "@/pages/EditWorkOrderFull";
import EditWorkOrderRedirect from "@/pages/EditWorkOrderRedirect";
import Customers from "@/pages/Customers";
import CustomerDatabase from "@/pages/CustomerDatabase";
import WOReports from "@/pages/WOReports";
import WorkOrderQuotation from "@/pages/WorkOrderQuotation";
import WorkOrderArchive from "@/pages/WorkOrderArchive";
import WorkOrderUnified from "@/pages/WorkOrderUnified";
import CreateQuotationForm from "@/pages/CreateQuotationForm";
import ProdMaterialInfo from "@/pages/ProdMaterialInfo";
import ProdMaterialDetail from "@/pages/ProdMaterialDetail";
import DieIDManagement from "@/pages/DieIDManagement";
import SparesDatabase from "@/pages/SparesDatabase";
import SparesOverview from "@/pages/SparesOverview";
import SpareDetail from "@/pages/SpareDetail";
import StockOut from "@/pages/StockOut";
import StoreDashboard from "@/pages/StoreDashboard";
import MachineMaintenance from "@/pages/MachineMaintenance";
import StoreDatabase from "@/pages/StoreDatabase";
import StoreInventory from "@/pages/StoreInventory";
import StoreLowStock from "@/pages/StoreLowStock";
import StorePOList from "@/pages/StorePOList";
import StorePurchaseOrder from "@/pages/StorePurchaseOrder";
import InventoryControl from "@/pages/InventoryControl";
import StoreReports from "@/pages/StoreReports";
import StorePredictor from "@/pages/StorePredictor";
import SPDLogin from "@/pages/SPDLogin";
import SPDLayout from "@/components/SPDLayout";
import SPDDashboard from "@/pages/SPDDashboard";
import SPDCreateOrder from "@/pages/SPDCreateOrder";
import SPDEditOrder from "@/pages/SPDEditOrder";
import SPDOrders from "@/pages/SPDOrders";
import SPDDisplay from "@/pages/SPDDisplay";
import DeliveryChallan from "@/pages/DeliveryChallan";
import LabelPreview from "@/pages/LabelPreview";
import ProductionTracking from "@/pages/ProductionTracking";
import ProductionTrackingDetail from "@/pages/ProductionTrackingDetail";
import { Toaster } from "@/components/ui/sonner";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import WorkOrderProgressMobile from "@/pages/WorkOrderProgressMobile";
import MaterialUsagePage from "@/pages/MaterialUsage";
import { ThemeProvider } from "@/context/ThemeContext";
import { SubscriptionProvider } from "@/context/SubscriptionContext";
import SubscriptionBanner from "@/components/SubscriptionBanner";
import FeatureGate from "@/components/FeatureGate";
import AdminSubscription from "@/pages/AdminSubscription";
import DatabaseMonitor from "@/components/DatabaseMonitor";
import AccountsLogin from "@/pages/AccountsLogin";
import AccountsLayout from "@/components/AccountsLayout";
import AccountsDashboard from "@/pages/AccountsDashboard";
import AccountsCustomers from "@/pages/AccountsCustomers";
import AccountsVendors from "@/pages/AccountsVendors";
import AccountsTaxRates from "@/pages/AccountsTaxRates";
import AccountsVoucherTypes from "@/pages/AccountsVoucherTypes";
import AccountsVouchers from "@/pages/AccountsVouchers";
import AccountsInvoiceSettings from "@/pages/AccountsInvoiceSettings";
import HRLogin from "@/pages/HRLogin";
import HRLayout from "@/components/HRLayout";
import HRDashboard from "@/pages/HRDashboard";
import HRAttendance from "@/pages/HRAttendance";
import HREmployeeSalary from "@/pages/HREmployeeSalary";
import AdminSecurity from "@/pages/AdminSecurity";
import AdminBackups from "@/pages/AdminBackups";
import HRIncrements from "@/pages/HRIncrements";
import HRPayroll from "@/pages/HRPayroll";
import HRReports from "@/pages/HRReports";
import HRAdvances from "@/pages/HRAdvances";
import HRManualAttendance from "@/pages/HRManualAttendance";

function App() {
  return (
    <ThemeProvider>
    <SubscriptionProvider>
    <div className="App">
      <BrowserRouter>
        <SubscriptionBanner />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/production" element={<ProductionAccess />} />
          <Route path="/employee" element={<FeatureGate feature="employee_clock_in"><EmployeeTimesheet /></FeatureGate>} />
          {/* Legacy /admin-login still works for direct URLs (uses unified login dialog) */}
          <Route path="/admin-login" element={<AdminLogin />} />
          {/* Role-specific login pages — restored Feb 2026 so each Pro module
              can ask for its own credentials when a Hub card is clicked. */}
          <Route path="/workorder-login" element={<WorkOrderLogin />} />
          <Route path="/store-login" element={<StoreLogin />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          
          <Route path="/spd-login" element={<SPDLogin />} />
          <Route path="/spd-display" element={<FeatureGate feature="spd"><SPDDisplay /></FeatureGate>} />
          
          {/* Public Label Preview - Accessible via QR code scan */}
          <Route path="/label/:id" element={<LabelPreview />} />
          
          {/* Mobile Work Order Progress - View only — Pro only */}
          <Route path="/wo-progress" element={<FeatureGate feature="wo_progress"><WorkOrderProgressMobile /></FeatureGate>} />

          {/* Material Usage entry — Core only (admin login required, no clock-in/out). */}
          <Route path="/material-usage" element={<MaterialUsagePage />} />
          
          {/* Admin Routes */}
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="timesheet" element={<FeatureGate feature="admin_timesheet"><Dashboard /></FeatureGate>} />
            <Route path="reports" element={<FeatureGate feature="admin_reports"><Reports /></FeatureGate>} />
            <Route path="cost-calculator" element={<FeatureGate feature="cost_calculator"><CostCalculator /></FeatureGate>} />
            <Route path="completion-history" element={<FeatureGate feature="admin_history"><CompletionHistory /></FeatureGate>} />
            <Route path="production-tracking" element={<ProductionTracking />} />
            <Route path="production-tracking/:woNumber" element={<ProductionTrackingDetail />} />
            <Route path="maintenance" element={<MachineMaintenance />} />
            <Route path="database-monitor" element={<DatabaseMonitor />} />
            <Route path="security" element={<AdminSecurity />} />
            <Route path="backups" element={<AdminBackups />} />
            <Route path="subscription" element={<AdminSubscription />} />
          </Route>
          
          {/* Work Order Routes */}
          {/* Main unified Work Order page - exact match */}
          <Route path="/workorder" element={<WorkOrderUnified />} />
          
          {/* Work Order sub-routes that need WorkOrderLayout wrapper */}
          <Route path="/workorder/view/:id" element={<ViewWorkOrder />} />
          <Route path="/workorder/edit/:id" element={<EditWorkOrderRedirect />} />
          <Route path="/workorder/create-new" element={<WorkOrderCreationNew />} />
          <Route path="/workorder/customers" element={<CustomerDatabase />} />
          <Route path="/workorder/customer-items" element={<Customers />} />
          <Route path="/workorder/reports" element={<WOReports />} />
          <Route path="/workorder/quotation" element={<FeatureGate feature="quotations"><WorkOrderQuotation /></FeatureGate>} />
          <Route path="/workorder/quotation/create" element={<FeatureGate feature="quotations"><CreateQuotationForm /></FeatureGate>} />
          <Route path="/workorder/prod-material" element={<ProdMaterialInfo />} />
          <Route path="/workorder/prod-material/:id" element={<ProdMaterialDetail />} />
          <Route path="/workorder/die-ids" element={<DieIDManagement />} />
          <Route path="/workorder/machines" element={<Machines />} />
          <Route path="/workorder/machines/spares-database" element={<SparesDatabase />} />
          <Route path="/workorder/machines/spares-overview" element={<SparesOverview />} />
          <Route path="/workorder/machines/:machineId/spare/:spareId" element={<SpareDetail />} />
          <Route path="/stock-out" element={<StockOut />} />

          {/* Store Routes */}
          <Route path="/store" element={<StoreLayout />}>
            <Route index element={<StoreDashboard />} />
            <Route path="dashboard" element={<StoreDashboard />} />
            <Route path="inventory" element={<StoreInventory />} />
            <Route path="database" element={<StoreDatabase />} />
            <Route path="low-stock" element={<StoreLowStock />} />
            <Route path="purchase-order" element={<StorePOList />} />
            <Route path="purchase-order/new" element={<StorePurchaseOrder />} />
            <Route path="inventory-control" element={<FeatureGate feature="inventory_control"><InventoryControl /></FeatureGate>} />
            <Route path="reports" element={<StoreReports />} />
            <Route path="predictor" element={<FeatureGate feature="cost_calculator"><StorePredictor /></FeatureGate>} />
            <Route path="delivery-challan" element={<FeatureGate feature="delivery_challans"><DeliveryChallan /></FeatureGate>} />
            <Route path="requests" element={<StoreDashboard />} />
          </Route>

          {/* SPD Routes — Pro only */}
          <Route path="/spd" element={<FeatureGate feature="spd"><SPDLayout /></FeatureGate>}>
            <Route index element={<SPDDashboard />} />
            <Route path="dashboard" element={<SPDDashboard />} />
            <Route path="create-order" element={<SPDCreateOrder />} />
            <Route path="edit-order/:orderId" element={<SPDEditOrder />} />
            <Route path="orders" element={<SPDOrders />} />
          </Route>

          {/* Accounts Routes */}
          <Route path="/accounts-login" element={<AccountsLogin />} />
          <Route path="/accounts" element={<FeatureGate feature="accounts"><AccountsLayout /></FeatureGate>}>
            <Route index element={<AccountsDashboard />} />
            <Route path="dashboard" element={<AccountsDashboard />} />
            <Route path="customers" element={<AccountsCustomers />} />
            <Route path="vendors" element={<AccountsVendors />} />
            <Route path="tax-rates" element={<AccountsTaxRates />} />
            <Route path="voucher-types" element={<AccountsVoucherTypes />} />
            <Route path="vouchers" element={<AccountsVouchers />} />
            <Route path="invoice-settings" element={<AccountsInvoiceSettings />} />
          </Route>

          {/* HR Routes */}
          <Route path="/hr-login" element={<HRLogin />} />
          <Route path="/hr" element={<HRLayout />}>
            <Route index element={<HRDashboard />} />
            <Route path="dashboard" element={<HRDashboard />} />
            <Route path="attendance" element={<HRAttendance />} />
            <Route path="manual-attendance" element={<HRManualAttendance />} />
            <Route path="employees" element={<HREmployeeSalary />} />
            <Route path="salary-config" element={<HREmployeeSalary />} />
            <Route path="increments" element={<HRIncrements />} />
            <Route path="payroll" element={<HRPayroll />} />
            <Route path="advances" element={<HRAdvances />} />
            <Route path="reports" element={<HRReports />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster />
      <PWAInstallPrompt />
    </div>
    </SubscriptionProvider>
    </ThemeProvider>
  );
}

export default App;